import { MDBCheckbox, MDBIcon } from "mdb-react-ui-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TreeView, { flattenTree, INode, ITreeViewOnSelectProps } from "react-accessible-treeview";
import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { TextInput } from "../../../components/core/inputs/TextInput";
import API from "../../../service/api";
import { useTranslation } from "react-i18next";
import { IFlatMetadata } from "react-accessible-treeview/dist/TreeView/utils";
import { Alert } from "../../../components/Alert";
import { DeleteConfirmModal } from "../../../components/modals/DeleteConfirmModal";

export interface PermissionEditProps {
    rightsId: number;
    maximal: any;
    minimal: any;
    canEdit: boolean;
    canDelete: boolean;
    onAdd: () => void;
    onEdit: () => void;
    onDelete: () => void;
}
export const PermissionEdit = ({rightsId, maximal, minimal, canEdit, canDelete, onAdd, onEdit, onDelete}: PermissionEditProps) => {
    const { t } = useTranslation();
    const data = useMemo(() => flattenTree({
        name: "",
        children: ObjectToTreeData(maximal)
    }), [maximal]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const [rights, setRights] = useState<any|null>(null);
    const [name, setName] = useState('');
    const [def, setDef] = useState({});
    const [changed, setChanged] = useState(false);
    const [loading, setLoading] = useState(false);
    const [protectedRight, setProtectedRight] = useState(false);
    const [error, setError] = useState('');
    const [deletePopup, setDeletePopup] = useState(false);
    const [savePopup, setSavePopup] = useState(false);

    const fetch = useCallback(async () => {
        setChanged(false);
        setError('');
        setName('');
        setSelectedIds([]);
        if (rightsId >= 0 && typeof rightsId == 'number'){
            setLoading(true);
            try {
                const r = await API.GET('/api/rights/'+rightsId);
                setName(r.Name);
                setProtectedRight(r.private);
                setDef(r.defObject);
                selectNodesByDefObject(r.defObject);
                setRights(r);
            }catch(err: any){
                setError(err.message);
            }
            finally{
                setLoading(false);
            }
        }
        else {
            setDef(minimal);
            setRights({});
            setProtectedRight(false);
        }
    }, [rightsId, minimal]);

    useEffect(() => {
        fetch();
    }, [rightsId]);

    const selectNode = useCallback((id: string, select: boolean) => {
        const node = data.find(el => el.id == id);
        if (!node) return;
        if (node.children.length == 0) {
            if (select) setSelectedIds(prev => ([...prev, id]));
            else setSelectedIds(prev => prev.filter(pid => pid !== id));
        }else node.children.forEach(child => selectNode(child.toString(), select));
    }, [data]);

    const isSelected = useCallback((id: string) => {
        const node = data.find(el => el.id == id);
        if (!node) return false;

        if (node.children.length == 0) return selectedIds.includes(id);
        else return node.children.every(child => isSelected(child.toString()));
    }, [data, selectedIds]);

    const isHalfSelected = useCallback((id: string) => {
        const node = data.find(el => el.id == id);
        if (!node) return false; 

        if (node.children.length == 0) return false;
        const selected = node.children.filter(child => isSelected(child.toString()) || isHalfSelected(child.toString())).length;
        return selected > 0 && selected < node.children.length;
    }, [data, selectedIds]);

    const selectNodesByDefObject = useCallback((def: any, parent?: string) => {
        Object.keys(def).forEach(key => {
            const id = parent ? `${parent}.${key}` : key;
            if (typeof def[key] == 'boolean' && def[key]) selectNode(id, true);
            else if (typeof def[key] == 'object') selectNodesByDefObject(def[key], id);
        });
    }, []);

    const selectedNodesToObject = useCallback(() => {
        const selectedObj: any = {};
        selectedIds.forEach(id => {
            const path = id.split('.').slice(0, -1);
            const leaf = id.split('.').slice(-1)[0];
            let node = selectedObj;
            path.forEach(route => {
                if (typeof node[route] == 'object') node = node[route];
                else {
                    node[route] = {};
                    node = node[route];
                }
            });
            node[leaf] = true;
        });
        return selectedObj;
    }, [selectedIds]);

    const insert = useCallback(() => {
        setLoading(true);
        API.POST('/api/rights', {
            Name: name,
            def: JSON.stringify(selectedNodesToObject())
        })
            .then(() => { onAdd(); fetch(); })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [selectedNodesToObject]);

    const update = useCallback(() => {
        setLoading(true);
        API.PATCH('/api/rights/'+rightsId, {
            Name: name,
            def: JSON.stringify(selectedNodesToObject())
        })
            .then(() => { onEdit(); fetch(); })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [rightsId, selectedNodesToObject, name]);

    if (!rights) return null;
    return (
        <div className="d-flex flex-column overflow-hidden card overflow-hidden" style={{minWidth: 350}}>

            <DeleteConfirmModal
                open={deletePopup}
                title={t('remove_permission')}
                message={t('remove_permission_prompt')}
                onCancel={() => setDeletePopup(false)}
                onConfirm={() => {
                    API.DELETE('/api/rights/'+rightsId).then(() => { onDelete(); onEdit(); }).catch(err => setError(err.message));
                    setDeletePopup(false);
                }}
            />

            <DeleteConfirmModal
                open={savePopup}
                title={t('save_permission')}
                message={t('save_permission_prompt')}
                onCancel={() => setSavePopup(false)}
                onConfirm={() => {
                    update();
                    setSavePopup(false);
                }}
                confirmText={t('save')}
            />

            <div className="d-flex flex-column overflow-hidden card-body">
                <div className="d-flex flex-column w-100 justify-content-center overflow-hidden">
                    <MDBIcon icon="fas fa-briefcase" size="4x" className="mb-2"/>
                    
                    {loading && <LoadingSpinner/>}
                    {!loading && <>
                        <h5 className="text-center">{rightsId !== -1 ? rights.Name : t('new_permissions')}</h5>

                        <div className="d-flex flex-column pt-1 pe-1">
                            {!protectedRight && (
                                <TextInput label={t('name')} icon="pen-to-square" value={name} onValueChange={val => { setName(val); setChanged(true); }}/>
                            )}
                        </div>
                    </>}

                    <div className="checkbox" style={{overflowY: 'auto'}}>
                        <TreeView
                            key={rightsId}
                            data={data}
                            className=""
                            multiSelect
                            // propagateSelect
                            // propagateSelectUpwards
                            // togglableSelect
                            selectedIds={selectedIds}
                            onSelect={({isSelected, isHalfSelected, element}) => {
                            }}
                            nodeRenderer={({
                                element,
                                isBranch,
                                isExpanded,
                                getNodeProps,
                                level,
                                handleExpand
                            }) => {
                                const selected = isSelected(element.id.toString());
                                const halfSelected = isHalfSelected(element.id.toString());
                                return (
                                    <div
                                        {...getNodeProps({ onClick: handleExpand })}
                                        style={{ marginLeft: 40 * (level - 1), listStyle: "none", listStyleType: "none", opacity: (!protectedRight && canEdit) ? 1 : 0.75 }}
                                        className="d-flex flex-row align-items-center"
                                    >
                                        {isBranch && <>
                                            {isExpanded && <span><MDBIcon icon={"caret-down"} fixed className="me-1"/></span>}
                                            {!isExpanded && <span><MDBIcon icon={"caret-right"} fixed className="me-1"/></span>}
                                        </>}
                                        <span
                                            className="me-1"
                                            style={{}}
                                            onClick={e => {
                                                if (canEdit && !protectedRight){
                                                    selectNode(element.id.toString(), !selected);
                                                    setChanged(true);
                                                }
                                                
                                                e.stopPropagation();
                                            }}
                                        >
                                            {selected && <span><MDBIcon icon="check-square" color="primary" size="lg"/></span>}
                                            {(!selected && !halfSelected) && <span><MDBIcon far icon="square" color="primary" size="lg"/></span>}
                                            {(!selected && halfSelected) && <span><MDBIcon icon="minus-square" color="primary" size="lg"/></span>}
                                        </span>

                                        <span className="name">{t(`permissions:${element.id.toString()}`)}</span>
                                    </div>
                                )
                            }}
                        />
                    </div>

                    <Alert type="danger" message={error}/>

                    <div className="d-flex flex-column">
                        {rightsId !== -1 && <>
                            { (!protectedRight && canEdit) && <button className="btn btn-primary mb-2" disabled={!changed || loading} onClick={() => setSavePopup(true)}>{t('save')}</button> }
                            { (!protectedRight && canDelete) && <button className="btn btn-danger" disabled={loading} onClick={() => setDeletePopup(true)}>{t('delete')}</button> }
                        </>}
                        {rightsId === -1 && <>
                            { !protectedRight && <button className="btn btn-primary mt-2" disabled={!changed || loading} onClick={() => insert()}>{t('add')}</button> }
                        </>}
                    </div>
                    
                </div>
            </div>
        </div>
    )
}

const ObjectToTreeData = (obj: any, parent?: string) => {
    return Object.keys(obj).map(name => {
        const id = parent ? `${parent}.${name}` : name;
        return {
            id,
            name,
            ...(typeof obj[name] == 'object' && {
                children: ObjectToTreeData(obj[name], id)
            })
        };
    });
}